import { useParams } from 'react-router-dom';

import { useEnrichments, useVerifications } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { ClaimIdChip } from 'src/components/editorial/ClaimIdChip';
import { DiagnosticList } from 'src/components/editorial/DiagnosticList';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { SourceCard } from 'src/components/editorial/SourceCard';
import { PublicationInstructionBadge, VerdictBadge } from 'src/components/editorial/VerdictBadge';
import { Badge } from 'src/components/ui/Badge';
import { Disclosure } from 'src/components/ui/Disclosure';
import { Icon } from 'src/components/ui/Icon';
import { Notice } from 'src/components/ui/Notice';
import { Skeleton } from 'src/components/ui/Skeleton';
import { VERIFICATION_STATUS } from 'src/lib/diagnostics';
import { EPISTEMIC_LABEL, EPISTEMIC_TONE, RISK_LABEL } from 'src/lib/reader';
import { verificationStatusTone, type Tone } from 'src/lib/tone';
import { formatDate } from 'src/lib/time';
import type {
  EditorialClaimAssessment,
  EditorialExternalFact,
  EditorialSource,
  EditorialVerification,
} from 'src/types/editorial';

/**
 * Answers: does the outside world support it?
 *
 * Composition §7 — verdict before evidence. Contradicted claims are never below the fold, and a
 * blocked verification renders its reason at primary weight because it is a finding, not a blank.
 */

const MATERIALITY_RANK: Record<string, number> = { critical: 0, important: 1, contextual: 2 };
const VERDICT_RANK: Record<string, number> = {
  contradicted: 0,
  partially_supported: 1,
  unresolved: 2,
  not_verifiable: 3,
  supported: 4,
  not_required: 5,
};

export function VerificationWorkspace() {
  const { streamId = '' } = useParams<{ streamId: string }>();
  const verifications = useVerifications(streamId);
  const enrichments = useEnrichments(streamId);

  const records = verifications.data?.verifications ?? [];
  const assessmentCount = records.reduce((total, record) => total + record.payload.claim_assessments.length, 0);

  // The section shell renders in every state — pending, error, empty — so the page never falls
  // back to a bare panel without its heading.
  return (
    <div className="page-wide">
      <header className="workspace-section-head" aria-labelledby="verification-title">
        <div className="workspace-section-head__copy">
          <p className="workspace-section-head__eyebrow">05 <span aria-hidden="true">/</span> CONTRASTE CON FUENTES</p>
          <h2 id="verification-title">Verificación</h2>
          <p>
            Contrasta los claims con fuentes externas y revisa qué se sostiene, qué sigue en disputa y cómo debe
            presentarse antes de publicar.
          </p>
        </div>
        <div className="workspace-section-head__stats" aria-label="Resumen de verificación">
          <div className="workspace-section-head__stat">
            <strong>{verifications.data ? records.length : '—'}</strong>
            <span>resultados</span>
          </div>
          <div className="workspace-section-head__stat">
            <strong>{verifications.data ? assessmentCount : '—'}</strong>
            <span>claims evaluados</span>
          </div>
        </div>
      </header>

      {verifications.isPending ? (
        <Skeleton variant="rows" count={6} />
      ) : verifications.isError ? (
        <LoadFailure title="No se pudo cargar la verificación" error={verifications.error} onRetry={verifications.refetch} />
      ) : records.length === 0 ? (
        <section className="verification-empty" aria-labelledby="verification-empty-title">
          <div className="verification-empty__mark" aria-hidden="true">
            <Icon name="search" />
          </div>
          <div className="verification-empty__copy">
            <span className="verification-empty__eyebrow">Sin resultados todavía</span>
            <h3 id="verification-empty-title">Esta transmisión aún no tiene resultados de verificación</h3>
            <p>
              Cuando se ejecute esta etapa, aquí aparecerán los veredictos por claim, las fuentes consultadas y el
              alcance recomendado para publicar.
            </p>
          </div>
        </section>
      ) : (
        records.map(verification => (
          <VerificationRecord
            key={verification.storyKey}
            verification={verification}
            enrichmentCount={
              enrichments.data?.enrichments.filter(item => item.storyKey === verification.storyKey).length ?? 0
            }
          />
        ))
      )}
    </div>
  );
}

function VerificationRecord({
  verification,
  enrichmentCount,
}: {
  verification: EditorialVerification;
  enrichmentCount: number;
}) {
  const payload = verification.payload;
  const assessments = sortAssessments(payload.claim_assessments);
  const blocked = payload.status.startsWith('blocked') || payload.status === 'research_failed';

  return (
    <article className="verification" id={`verification-${verification.storyKey}`}>
      <header className="verification__head">
        <span className="verification__story-id mono">Historia · {verification.storyId.slice(-8)}</span>
        <Badge tone={verificationStatusTone(payload.status)} title={VERIFICATION_STATUS[payload.status]?.hint}>
          {VERIFICATION_STATUS[payload.status]?.label ?? payload.status}
        </Badge>
        <span className="verification__asof mono">al {formatDate(payload.as_of)}</span>
      </header>

      {/* A blocked verification is a finding, not an empty state (Composition §7). */}
      {blocked && (
        <Notice
          tone="contradicted"
          title={VERIFICATION_STATUS[payload.status]?.label ?? `Verificación detenida: ${payload.status}`}
          role="alert"
        >
          <p>{VERIFICATION_STATUS[payload.status]?.hint ?? 'El pipeline no registró un motivo detallado.'}</p>
        </Notice>
      )}

      <DiagnosticList codes={payload.warnings} title="Advertencias de la verificación" />

      {assessments.length === 0 ? (
        <AbsenceState kind="empty" what="La verificación corrió y no evaluó ningún claim." />
      ) : (
        <div className="rubric-scroll">
          <table className="verdicts">
            <thead>
              <tr>
                <th scope="col">Claim</th>
                <th scope="col">Veredicto</th>
                <th scope="col">Qué hacer al publicar</th>
                <th scope="col" className="verdicts__num">
                  Evidencias
                </th>
                <th scope="col" className="verdicts__freshness">
                  Vigencia
                </th>
              </tr>
            </thead>
            <tbody>
              {assessments.map(assessment => (
                <tr key={assessment.claim_id} data-verdict={assessment.verdict}>
                  <td>
                    <ClaimIdChip claimId={assessment.claim_id} />
                    <span className="verdicts__materiality">{assessment.materiality}</span>
                  </td>
                  <td>
                    <VerdictBadge verdict={assessment.verdict} confidence={assessment.confidence} />
                    {/* The instruction badge fires on this text — without it, "state with reduced
                        scope" has no visible scope to back it (Composition §7). */}
                    {assessment.supported_scope && <p className="verdicts__scope">{assessment.supported_scope}</p>}
                    {assessment.unsupported_scope && (
                      <p className="verdicts__scope verdicts__scope--unsupported">
                        no cubre: {assessment.unsupported_scope}
                      </p>
                    )}
                    {assessment.notes.length > 0 && (
                      <ul className="verdicts__notes">
                        {assessment.notes.map(note => (
                          <li key={note}>{note}</li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td>
                    <PublicationInstructionBadge instruction={assessment.publication_instruction} />
                  </td>
                  <td className="verdicts__num">
                    {assessment.evidence_ids.length}
                    {assessment.contradicting_evidence_ids.length > 0 && (
                      <span className="verdicts__against">
                        {' '}
                        / {assessment.contradicting_evidence_ids.length} en contra
                      </span>
                    )}
                  </td>
                  <td className="mono verdicts__freshness">
                    <ClaimFreshness verifiedAt={assessment.verified_at} expiresAt={assessment.expires_at} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {payload.conflicts.length > 0 && (
        <section className="verification__conflicts">
          <h3 className="section-title">Conflictos</h3>
          <ul>
            {[...payload.conflicts]
              .sort((a, b) => Number(b.must_appear_in_note) - Number(a.must_appear_in_note))
              .map(conflict => (
                <li key={conflict.conflict_id}>
                  {conflict.must_appear_in_note && <Badge tone="contradicted">debe aparecer en la nota</Badge>}
                  <span>{conflict.description}</span>
                </li>
              ))}
          </ul>
        </section>
      )}

      {payload.proposed_corrections.length > 0 && (
        <Disclosure summary="Correcciones propuestas" count={payload.proposed_corrections.length}>
          <ul className="corrections">
            {payload.proposed_corrections.map(correction => (
              <li key={correction.correction_id}>
                <span className="corrections__field mono">{correction.field}</span>
                <span className="corrections__before">{correction.before}</span>
                <span aria-hidden="true">→</span>
                <span className="corrections__after">{correction.after}</span>
                <Badge tone={correction.status === 'accepted' ? 'supported' : 'unresolved'}>{correction.status}</Badge>
                <p className="corrections__reason">{correction.reason}</p>
              </li>
            ))}
          </ul>
        </Disclosure>
      )}

      {payload.entity_resolutions.length > 0 && (
        <Disclosure summary="Entidades corregidas" count={payload.entity_resolutions.length}>
          <ul className="corrections">
            {payload.entity_resolutions.map(resolution => (
              <li key={resolution.correction_id}>
                <span className="corrections__field mono">{resolution.field}</span>
                <span className="corrections__before">{resolution.before}</span>
                <span aria-hidden="true">→</span>
                <span className="corrections__after">{resolution.after}</span>
                <Badge tone={resolution.status === 'accepted' ? 'supported' : 'unresolved'}>{resolution.status}</Badge>
                <p className="corrections__reason">{resolution.reason}</p>
              </li>
            ))}
          </ul>
        </Disclosure>
      )}

      {payload.external_facts.length > 0 && (
        <Disclosure summary="Hechos externos aportados" count={payload.external_facts.length}>
          <ul className="facts">
            {payload.external_facts.map(fact => (
              <ExternalFactRow key={fact.external_fact_id} fact={fact} sources={payload.sources} />
            ))}
          </ul>
        </Disclosure>
      )}

      {/* Guide §8.2. Rendered only when one exists — an editor seeing no section should read "this
          note may not claim anyone failed to answer", which is what its absence now means. */}
      {(payload.coverage_findings ?? []).length > 0 && (
        <Disclosure summary="Ausencias acreditadas" count={(payload.coverage_findings ?? []).length}>
          <ul className="facts">
            {(payload.coverage_findings ?? []).map(finding => (
              <li key={finding.coverage_finding_id}>
                <p>{finding.allowed_public_expression}</p>
                <p className="facts__meta">
                  {finding.scope} · {finding.transcript_segment_ids.length} segmentos revisados · confianza{' '}
                  {finding.confidence}
                </p>
                {finding.forbidden_expression && (
                  <p className="facts__forbidden">Prohibido: «{finding.forbidden_expression}»</p>
                )}
              </li>
            ))}
          </ul>
        </Disclosure>
      )}

      <section className="verification__provenance" aria-labelledby={`provenance-${verification.storyKey}`}>
        <header className="verification__provenance-head">
          <div>
            <h3 id={`provenance-${verification.storyKey}`}>Procedencia y trazabilidad</h3>
            <p>Fuentes y proceso que respaldan esta revisión.</p>
          </div>
          <ul className="verification__provenance-stats" aria-label="Resumen de procedencia">
            <li><strong>{payload.sources.length}</strong><span>fuentes</span></li>
            <li><strong>{payload.evidence.length}</strong><span>evidencias</span></li>
            <li><strong>{payload.research_trace.length}</strong><span>búsquedas</span></li>
            <li><strong>{enrichmentCount}</strong><span>perfiles enriquecidos</span></li>
          </ul>
        </header>

        {payload.sources.length > 0 && (
          <Disclosure summary="Fuentes" count={payload.sources.length}>
            <div className="sources">
              {payload.sources.map(source => (
                <SourceCard key={source.source_id} source={source} />
              ))}
            </div>
          </Disclosure>
        )}

        {payload.research_trace.length > 0 && (
          <Disclosure summary="Traza de investigación" count={payload.research_trace.length}>
            <ul className="trace">
              {payload.research_trace.map(trace => (
                <li key={trace.question_id}>
                  <p className="trace__question">{trace.question}</p>
                  <p className="trace__meta mono">
                    {trace.provider} {trace.model} · {trace.candidates_returned} candidatos ·{' '}
                    {trace.candidates_rejected.length} descartados · {trace.documents_fetched} documentos
                  </p>
                  {trace.queries_run.length > 0 && (
                    <p className="trace__queries mono">{trace.queries_run.join(' | ')}</p>
                  )}
                  {trace.candidates_rejected.length > 0 && (
                    <ul className="trace__rejected">
                      {trace.candidates_rejected.map(rejected => (
                        <li key={rejected.url}>
                          <span className="mono">{rejected.reason}</span> {rejected.url}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </Disclosure>
        )}
      </section>
    </article>
  );
}

/** Claim verdicts are cached by `as_of_bucket` precisely so they go stale — an editor publishing
 *  off an expired verdict is trusting a fact this pipeline already flagged as due for a recheck. */
/**
 * One external fact, with what a sentence resting on it is allowed to assert.
 *
 * The publication instruction alone was not enough: two facts can both say
 * `state_with_reduced_scope` and mean "an outlet read the file" and "one party alleges", which are
 * different sentences. The chip is the difference, and the qualifier below it is the words the
 * drafter is obliged to keep.
 */
function ExternalFactRow({ fact, sources }: { fact: EditorialExternalFact; sources: EditorialSource[] }) {
  const status = fact.epistemic_status;
  const risk = RISK_LABEL[fact.risk_level ?? 'low'];
  const families = fact.source_family_ids?.length ?? 0;
  const publishers = fact.source_ids
    .map(id => sources.find(source => source.source_id === id)?.publisher)
    .filter((publisher): publisher is string => Boolean(publisher));

  return (
    <li>
      <p>{fact.statement}</p>
      <p className="facts__chips">
        {status && (
          <Badge tone={(EPISTEMIC_TONE[status] ?? 'unresolved') as Tone}>{EPISTEMIC_LABEL[status] ?? status}</Badge>
        )}
        <PublicationInstructionBadge instruction={fact.publication_instruction} />
        {risk && <Badge tone="contradicted">{risk}</Badge>}
        {fact.human_review_required && <Badge tone="contradicted">requiere revisión humana</Badge>}
        {fact.stale && <Badge tone="partial">fuente fuera de vigencia</Badge>}
      </p>
      {(publishers.length > 0 || families > 0) && (
        <p className="facts__meta">
          {publishers.join(' · ')}
          {/* Independent origins, not urls: ten portals reprinting one bulletin are one. */}
          {families > 0 &&
            `${publishers.length > 0 ? ' · ' : ''}${families} procedencia${families === 1 ? '' : 's'} independiente${
              families === 1 ? '' : 's'
            }`}
        </p>
      )}
      {fact.minimum_public_qualifier && (
        <p className="facts__meta">Matiz mínimo obligatorio: «{fact.minimum_public_qualifier}»</p>
      )}
      {(fact.forbidden_upgrades ?? []).length > 0 && (
        <p className="facts__forbidden">No puede decir: {(fact.forbidden_upgrades ?? []).join(' · ')}</p>
      )}
      {(fact.required_counterweights ?? []).length > 0 && (
        <p className="facts__meta">
          Contrapunto obligatorio: {(fact.required_counterweights ?? []).join(', ')}
        </p>
      )}
    </li>
  );
}

function ClaimFreshness({ verifiedAt, expiresAt }: { verifiedAt: string; expiresAt: string | null }) {
  const stale = expiresAt !== null && new Date(expiresAt) < new Date();
  return (
    <>
      {formatDate(verifiedAt)}
      {expiresAt && <span className={stale ? 'verdicts__expiry--stale' : ''}>{stale ? ' vencido' : ` vence ${formatDate(expiresAt)}`}</span>}
    </>
  );
}

/** Materiality, then verdict severity — a contradicted critical claim is never below the fold. */
function sortAssessments(assessments: EditorialClaimAssessment[]): EditorialClaimAssessment[] {
  return [...assessments].sort((a, b) => {
    const byMateriality = (MATERIALITY_RANK[a.materiality] ?? 9) - (MATERIALITY_RANK[b.materiality] ?? 9);
    if (byMateriality !== 0) return byMateriality;
    const byVerdict = (VERDICT_RANK[a.verdict] ?? 9) - (VERDICT_RANK[b.verdict] ?? 9);
    return byVerdict !== 0 ? byVerdict : a.claim_id.localeCompare(b.claim_id);
  });
}
